import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../config/api";

/* ================= PASSWORD VALIDATION ================= */
const isStrongPassword = (password) => {
  // min 8 chars, 1 uppercase, 1 lowercase, 1 digit
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password);
};

export default function Signup() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignup = async () => {
    // ================= BASIC VALIDATION =================
    if (!name || !email || !password) {
      return alert("All fields required");
    }

    if (!isStrongPassword(password)) {
      return alert(
        "Password must be at least 8 characters long and include:\n" +
          "- One uppercase letter\n" +
          "- One lowercase letter\n" +
          "- One number"
      );
    }
    // ====================================================

    try {
      setLoading(true);

      // 🔥 ONLY CHANGE: signup → send-otp
      const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        return alert(data.message || "Failed to send OTP");
      }

      // ✅ DIRECT VERIFY OTP PAGE
      navigate("/verify-otp", { state: { email } });

    } catch (err) {
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2
      bg-gradient-to-br from-black via-gray-950 to-black text-white">

      {/* ================= LEFT INFO PANEL ================= */}
      <div className="hidden lg:flex flex-col justify-center px-20
        relative overflow-hidden
        bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">

        <div className="absolute inset-0
          bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.12),transparent_60%)]" />

        <div className="relative z-10 max-w-lg">
          <div className="mb-6 inline-flex items-center gap-2
            bg-black/40 border border-blue-500/30
            px-4 py-1.5 rounded-full text-sm text-blue-300">
            🚀 Learn • 🧪 Practice • 🏆 Compete
          </div>

          <h1 className="text-5xl font-extrabold leading-tight mb-6">
            CodemintX
          </h1>

          <p className="text-lg text-gray-300 mb-8">
            Build real skills with a{" "}
            <span className="font-semibold text-white">
              secure, exam-ready coding platform
            </span>{" "}
            designed for students and institutions.
          </p>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
            <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
              🔐 Secure exam environment
            </div>
            <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
              💻 Live compiler & labs
            </div>
            <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
              📚 Structured courses
            </div>
            <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
              📊 Progress & analytics
            </div>
          </div>
        </div>
      </div>

      {/* ================= RIGHT SIGNUP PANEL ================= */}
      <div className="flex items-center justify-center px-6">
        <div className="bg-gray-900/80 backdrop-blur
          p-8 rounded-2xl w-full max-w-md
          border border-gray-800 shadow-2xl">

          <h2 className="text-2xl font-bold mb-1 text-center">
            Create Account
          </h2>
          <p className="text-sm text-gray-400 mb-6 text-center">
            Join StudyPortal today
          </p>

          <input
            placeholder="Full Name"
            className="w-full p-3 mb-4 bg-gray-800 rounded-lg outline-none
            focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setName(e.target.value)}
          />

          <input
            type="email"
            placeholder="Email address"
            className="w-full p-3 mb-4 bg-gray-800 rounded-lg outline-none
            focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 mb-6 bg-gray-800 rounded-lg outline-none
            focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            disabled={loading}
            onClick={handleSignup}
            className={`w-full py-3 rounded-lg font-semibold transition
            ${loading
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {loading ? "Sending OTP..." : "Sign Up"}
          </button>

          <p className="text-sm mt-4 text-gray-400 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-400 hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
